import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, FileText } from "lucide-react";

const SECTIONS = [
  {
    n: 1,
    title: "Purpose",
    body:
      "THE_MINDSTEIN is an AI-assisted research and idea platform provided for informational and entertainment purposes only. It is not legal, medical, financial, or professional advice.",
  },
  {
    n: 2,
    title: "Accounts and access",
    body:
      "You must sign in to use protected features. Access may be granted through a paid subscription or active free trial. You are responsible for keeping your login details secure.",
  },
  {
    n: 3,
    title: "Acceptable use",
    body:
      "You agree not to misuse the service, attempt to bypass access controls, scrape data without permission, or use the platform for unlawful purposes.",
  },
  {
    n: 4,
    title: "AI content",
    body:
      "The platform may generate automated replies, summaries, rankings, and suggestions. We do not guarantee that content is complete, accurate, or suitable for a specific purpose. You should verify important information independently.",
  },
  {
    n: 5,
    title: "Subscriptions and trials",
    body:
      "Trial access and subscription access are controlled by your account status and the plan you select. We may update features, pricing, or access rules over time.",
  },
  {
    n: "5.0",
    title: "Early Access Package — one-time $100 promotional purchase",
    body:
      "THE_MINDSTEIN Early Access Package is a one-time, non-refundable promotional purchase priced at $100 that grants 175 MindStein usages shared across up to 5 verified emails (buyer plus up to 4 additional teammates). To be eligible, the buyer must (a) verify their account, and (b) complete the 5-use free trial. This is an ongoing promotion capped at a rolling window of 500 seats. Once an email appears on ANY Early Access Package — as buyer or as a member — that email is permanently blocked from purchasing this promotion again. By completing the purchase you agree to these terms: verification and trial-completion are strict eligibility requirements; the $100 fee is non-refundable; the offer is one-time per email; and the 175 usages are shared across the package and do not expire but do not renew automatically. There are no subscription commitments — this is a one-time promotional offer to support our architecture's journey from prototype to production.",
  },
  {
    n: 5.1,
    title: "30-day subscription honour (monthly plans)",
    body:
      "Once you complete the FIRST bot use after subscribing to a monthly plan (Starter · Elite · Pro · Team), a 30-day billing cycle is activated and honoured to prevent misuse. You may cancel to prevent renewal at any time BEFORE the next 30-day cycle. Refunds are not issued inside an active monthly cycle once the bot has been used.",
  },
  {
    n: 5.2,
    title: "Annual plans — 30-day refund window (Elite Annual · Pro Annual)",
    body:
      "The first-bot-use rule also applies to annual plans. Annual plans carry a 30-day refund window from that first use. Inside the window, refunds are processed as: one month charged at the equivalent monthly-tier price ($29.99 for Elite Annual, $49.99 for Pro Annual), the remainder of the annual fee refunded. Outside the window, the annual subscription is honoured to term with cancel-anytime-before-renewal.",
  },
  {
    n: 5.3,
    title: "Prohibited use — arms, military, ITAR",
    body:
      "MindStein STRICTLY PROHIBITS any use related to arms, weapons, ammunition, ordnance, military equipment, defense aerospace systems, or any activity violating US export controls (ITAR / EAR / USML) or Canadian export-control laws. Users attempting these uses will be permanently banned, held personally liable, and their identifying information (email, IP, device fingerprints, activity logs) will be disclosed to law-enforcement agencies in any jurisdiction under lawful warrant, subpoena, or investigative request without additional notice to the user.",
  },
  {
    n: 6,
    title: "Intellectual property",
    body:
      "THE_MINDSTEIN and its software belong to us or our licensors. You may not copy, resell, or reverse engineer the service. Content you upload remains yours, but you give us permission to process it to provide the service.",
  },
  {
    n: 7,
    title: "Liability",
    body:
      "The service is provided \u201cas is\u201d and \u201cas available\u201d. To the fullest extent allowed by law, we are not responsible for losses caused by use of the platform, AI outputs, service downtime, or third-party source errors.",
  },
  {
    n: 8,
    title: "Changes",
    body:
      "We may update these Terms from time to time. Continued use of the service means you accept the updated Terms.",
  },
  {
    n: 9,
    title: "Contact",
    body:
      "For questions about these Terms, contact the site owner or operator.",
  },
  {
    n: 10,
    title: "Funding and jurisdictional compliance",
    body:
      "If you intend to use THE_MINDSTEIN — its outputs, ideas, innovation signals, ranked sources, or bot conversations — to raise capital, solicit investors, launch a security or offering, apply for grants, or otherwise seek funding, you are solely responsible for complying with all applicable laws in your jurisdiction (including securities, disclosure, anti-fraud, tax, and licensing rules). THE_MINDSTEIN is not liable for any funding activity, investor relationship, regulatory filing, or offering you conduct. You must do your own due diligence and consult qualified legal and financial counsel before acting on anything surfaced by this platform.",
  },
  {
    n: 11,
    title: "Flexibility Subscription Terms & Rebate Policy (Addendum)",
    body:
      "Activation Rebate — upon formal commitment to any paid subscription tier, the account is automatically credited a 50% rebate against the first month's standard rate (valid for up to three bot deployments per account). 30-Day Cancellation & Grace Period — annual subscriptions may be cancelled within 30 days of the first bot use; the account is charged one month at the equivalent monthly-tier rate and the remainder is fully refunded. First-Renewal Bonus — annual plans (Elite Annual, Pro Annual) and Team plans receive an automatic +100 free MindStein usages on the first successful renewal (one-time offer per account). $0.01 Verification — a one-cent authorisation is placed on the payment method to verify the card belongs to a real person before the 7-day free trial is activated; the authorisation is auto-refunded on the same request. All rebate and refund settlements are processed through Stripe; please allow 5-10 business days for funds to appear in your account.",
  },
  {
    n: 12,
    title: "Team Plan Policy (Phase 28)",
    body:
      "Team Monthly ($149 / mo · 5 seats · +$49 per extra seat) and Team Unlimited ($249 / mo · unlimited seats) both provide a single shared pool of 150 MindStein usages per 30 days, plus a one-time +100 welcome bonus credited on commit. On the second successful monthly renewal, the account is credited a bonus of +50 uses per 5 verified seats (one-time). On the third successful monthly renewal, the account is credited a flat +100 free MindStein usages (one-time; applies to both Team Monthly and Team Unlimited). Team plans DO NOT receive the 3-deployment Activation Rebate — team economics are already reflected in the base price. If a monthly commitment is not desired, a flat pay-once $100 pack (150 MindStein usages) is available at any time via the top-up shelf; no subscription is created, no bonus multipliers apply, and the uses never expire.",
  },
  {
    n: 13,
    title: "Customer Care Commitment — protecting your original work",
    body:
      "As part of MindStein's customer care program, every verified output is protected by due-diligence tooling: SHA-256 content hashing with HMAC-signed timestamps (OneStamp) providing evidence of prior art for your ideas and synthesis results, plus citation and source verification (including DOI-level checks) on referenced material. These measures are provided automatically, at no additional cost, to help you document when your original work existed and what evidence it was built on.",
  },
  {
    n: 14,
    title: "Intellectual property disclaimer — your responsibilities",
    body:
      "MindStein's timestamping, hashing, and citation-verification tools are provided as good-faith protective measures only — they are NOT legal advice, NOT a patent or copyright filing, and NOT a security guarantee of any kind. You remain solely responsible for your own intellectual property filings (e.g., with the USPTO, CIPO, EPO, or WIPO/PCT), for assessing the legal sufficiency of any prior-art evidence, and for your own security practices, including safeguarding credentials, PINs, and exported documents. MindStein and Alberta 2639389 LTD accept no liability for lost, unregistered, or contested intellectual property rights.",
  },
  {
    n: 15,
    title: "SHA-256 timestamping — good-faith proof of creation only",
    body:
      "MindStein's SHA-256 timestamping feature records the date and time of original work as a good-faith proof of creation tool only. It is not a substitute for formal intellectual property protection. Users are solely responsible for conducting their own patent searches, consulting registered patent attorneys in their jurisdiction, and filing any IP applications independently. MindStein does not provide legal advice and makes no representation that timestamped work is original, unpatented, or legally protectable.",
    links: [
      { label: "USPTO (United States)", url: "https://www.uspto.gov" },
      { label: "CIPO (Canada)", url: "https://www.ic.gc.ca" },
      { label: "EPO (Europe)", url: "https://www.epo.org" },
      { label: "WIPO (International / PCT)", url: "https://www.wipo.int" },
    ],
    linksIntro: "Major patent offices for your guidance:",
  },
  {
    n: 16,
    title: "General disclaimer — due-diligence resource only",
    body:
      "MindStein provides synthesis and research tools as a due-diligence resource only. Users remain solely responsible for verifying all outputs, protecting their own intellectual property, and seeking independent legal and professional advice for any decisions based on platform outputs. MindStein makes no warranty as to the accuracy, completeness, originality, or legal sufficiency of any output and accepts no liability for decisions made in reliance on them.",
  },
  {
    n: 17,
    title: "Customer care commitment — good-faith protective measures",
    body:
      "MindStein's SHA-256 timestamping, DOI verification, credibility ranking, encrypted email chain, and Research Library search feature are provided as good-faith protective measures to support users in documenting, searching, and protecting their original work. These measures are offered as helpful tooling — not as legal advice or a guarantee of protection — and are provided automatically as part of MindStein's customer care program.",
  },
];

export default function LegalPage() {
  return (
    <div
      data-testid="legal-page"
      className="min-h-screen bg-[#0A0A0A] text-white relative"
    >
      <div aria-hidden className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <header className="relative z-10 border-b border-[#27272A] bg-[#0A0A0A]/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center gap-3">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="flex items-center gap-2 font-mono text-zinc-400 hover:text-white text-[12px]"
          >
            <ArrowLeft size={14} />
            <span className="inline-block w-2 h-2 bg-amber-400 animate-pulse" />
            <span className="text-base font-extrabold tracking-tight text-white">
              THE_MINDSTEIN
            </span>
          </Link>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-3">
            <Link
              to="/privacy"
              data-testid="legal-link-privacy"
              className="text-emerald-300 hover:text-white"
            >
              PRIVACY_POLICY_AND_OTHER_LEGAL_CONDITIONS →
            </Link>
            <span>LEGAL · DISCLAIMER · TERMS · v1.1</span>
          </span>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* DISCLAIMER */}
        <div
          data-testid="legal-disclaimer-block"
          className="border border-amber-500/40 bg-amber-500/[0.05] p-5"
        >
          <div className="inline-flex items-center gap-2 mb-3">
            <ShieldAlert size={14} className="text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
              DISCLAIMER
            </span>
          </div>
          <p
            data-testid="h-disclaimer"
            className="text-[14px] text-zinc-200 leading-relaxed"
          >
            <span className="font-mono text-white">THE_MINDSTEIN</span> uses
            AI-generated replies and automated source ranking for{" "}
            <span className="text-white">informational, research, and entertainment purposes only</span>.
            The content on this site is intended to be helpful and fun, but it is{" "}
            <span className="text-white">not legal, medical, financial, or professional advice</span>.
            Users should independently verify any important information before
            relying on it.
          </p>
        </div>

        {/* TERMS OF SERVICE */}
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={14} className="text-amber-400" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
              TERMS OF SERVICE · v1.0
            </span>
            <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-zinc-500">
              Updated {new Date().toISOString().slice(0, 10)}
            </span>
          </div>
          <h1
            data-testid="h-tos"
            className="font-mono text-2xl sm:text-3xl tracking-tight"
          >
            Terms of Service
          </h1>
          <p className="mt-3 text-zinc-400 text-[13px]">
            By using THE_MINDSTEIN, you agree to these Terms.
          </p>

          <ol
            data-testid="legal-tos-list"
            className="mt-6 space-y-4"
          >
            {SECTIONS.map((s) => (
              <li
                key={s.n}
                data-testid={`legal-tos-item-${s.n}`}
                className="border border-[#27272A] bg-[#0E0E0E] p-4"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-amber-400 text-lg tabnum">
                    {s.n}.
                  </span>
                  <h2 className="font-mono text-white text-[14px] tracking-tight">
                    {s.title}
                  </h2>
                </div>
                <p className="mt-2 text-zinc-300 text-[13px] leading-relaxed pl-7">
                  {s.body}
                </p>
                {s.links && (
                  <div className="mt-3 pl-7" data-testid={`legal-tos-links-${s.n}`}>
                    {s.linksIntro && (
                      <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                        {s.linksIntro}
                      </div>
                    )}
                    <ul className="mt-1 space-y-1">
                      {s.links.map((l) => (
                        <li key={l.url}>
                          <a
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[13px] text-amber-300 hover:text-amber-200 underline underline-offset-2 font-mono"
                          >
                            {l.label} — {l.url.replace("https://www.", "")}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>
      </main>
    </div>
  );
}
